import type * as React from 'react';
import { type ExternalToast, Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

import { Icon } from '@gitbook/spine-icons';
import { HTTPError, throwIfNotExposable } from '@gitbook/util-debug';

import { LoadingIndicator } from '../Loading';
import { Portal } from '../Portal';
import { type IconDefinition, renderIcon } from '../util';
import styles from './Toaster.module.css';

export function Toaster() {
    return (
        <Portal>
            <SonnerToaster
                className={styles.toaster}
                toastOptions={{
                    classNames: {
                        toast: styles.toast,
                        content: styles.content,
                        icon: styles.icon,
                        description: styles.description,
                        closeButton: styles.closeButton,
                        actionButton: styles.actionButton,
                        success: styles.success,
                        error: styles.error,
                    },
                    closeButton: true,
                }}
                icons={{
                    success: <Icon.CheckCircle />,
                    error: <Icon.Alert />,
                    loading: <LoadingIndicator />,
                }}
            />
        </Portal>
    );
}

export type ToastOptions = {
    icon?: IconDefinition | undefined;
    description?: React.ReactNode | undefined;
    action?: React.ReactNode | undefined;
    id?: string | number | undefined;
    closeButton?: boolean | undefined;
    persist?: boolean | undefined;
};

type ToastSuccessOptions = Omit<ToastOptions, 'icon' | 'closeButton'>;
type ToastErrorOptions = Omit<ToastOptions, 'icon' | 'closeButton' | 'duration'> & {
    /**
     * Indicates if the error is critical and should be shown as an un-dismissible error.
     */
    critical?: boolean | undefined;
};
type ToastLoadingOptions = Omit<ToastOptions, 'icon' | 'closeButton' | 'duration'>;

type PromiseT<Data = any> = Promise<Data> | (() => Promise<Data>);
type PromiseTResult<Data = any> =
    | string
    | React.ReactNode
    | ((data: Data) => React.ReactNode | string | Promise<React.ReactNode | string>);
type PromiseExternalToast = Omit<ToastOptions, 'description'>;
export type ToastPromiseData<ToastData = any> = PromiseExternalToast & {
    loading?: string | React.ReactNode;
    success?: PromiseTResult<ToastData>;
    error?: PromiseTResult;
    description?: PromiseTResult;
    finally?: () => void | Promise<void>;
};

export type ToastCatchOptions = {
    /**
     * Callback to run when the promise is rejected.
     */
    onReject?: (error: unknown) => void;
};

/**
 * Show a toast.
 */
type Toast = ((message: React.ReactNode, options?: ToastOptions) => string | number) & {
    /**
     * Show a success toast.
     */
    success: (message: React.ReactNode, options?: ToastSuccessOptions) => string | number;

    /**
     * Show an error toast.
     * Consider using `exception` instead if wrapping an user action that can generate expected exposable errors.
     */
    error: (
        message: React.ReactNode,
        error: Error | null,
        data?: ToastErrorOptions
    ) => string | number;

    /**
     * Catch an error and show a toast if it happens.
     * If the error is critical, it will be thrown.
     */

    catch: <T>(
        fn: () => T | Promise<T>,
        message: React.ReactNode | ((error: Error) => void),
        options?: ToastCatchOptions
    ) => Promise<T | undefined>;

    /**
     * Show Promise-aware toast.
     */
    promise: <ToastData>(
        promise: PromiseT<ToastData>,
        data?: ToastPromiseData<ToastData>
    ) =>
        | (string & {
              unwrap: () => Promise<ToastData>;
          })
        | (number & {
              unwrap: () => Promise<ToastData>;
          })
        | {
              unwrap: () => Promise<ToastData>;
          };

    /**
     * Show a loading toast.
     */
    loading: (message: React.ReactNode, options?: ToastLoadingOptions) => string | number;

    /**
     * Show an info toast.
     */
    info: (message: React.ReactNode, options?: ToastOptions) => string | number;

    /**
     * Dismiss a toast by its id or all toasts if no id is provided.
     */
    dismiss: (id?: number | string) => string | number;
};

function adaptOptions(options?: ToastOptions): ExternalToast | undefined {
    if (!options) {
        return undefined;
    }

    return {
        ...options,
        icon: options.icon ? renderIcon(options.icon) : undefined,
        duration: options.persist ? Number.POSITIVE_INFINITY : options.action ? 8000 : undefined,
    };
}

function toast(message: React.ReactNode, options?: ToastOptions) {
    return sonnerToast(message, adaptOptions(options));
}

const success: Toast['success'] = (message, options) => {
    return sonnerToast.success(message, adaptOptions(options));
};

const error: Toast['error'] = (message, error, options) => {
    const description = (() => {
        if (options?.description !== undefined) {
            return options.description;
        }
        if (error instanceof HTTPError) {
            return error.expose ? error.message : undefined;
        }
        return undefined;
    })();

    const adaptedOptions = adaptOptions({ ...options, description });

    return sonnerToast.error(message, {
        ...adaptedOptions,
        duration: options?.critical ? Number.POSITIVE_INFINITY : (adaptedOptions?.duration ?? 5000),
    });
};

const catchFn: Toast['catch'] = async (fn, messageOrErrorHandler, options) => {
    try {
        return await fn();
    } catch (error: unknown) {
        if (error instanceof Error) {
            if (typeof messageOrErrorHandler === 'function') {
                messageOrErrorHandler(error);
            } else {
                toast.error(messageOrErrorHandler, error);
            }
        }
        options?.onReject?.(error);
        throwIfNotExposable(error);
    }
};

const promise: Toast['promise'] = (promise, options) => {
    return sonnerToast.promise(promise, {
        ...options,
        icon: options?.icon ? renderIcon(options?.icon) : undefined,
        error: (error: unknown) => {
            if (error instanceof HTTPError && error.expose) {
                return error.message;
            }
            return 'An unexpected error occurred';
        },
    });
};

const loading: Toast['loading'] = (message, options) => {
    return sonnerToast.loading(message, {
        ...adaptOptions(options),
        closeButton: false,
        duration: Number.POSITIVE_INFINITY,
    });
};

const info: Toast['info'] = (message, options) => {
    return sonnerToast.info(message, adaptOptions(options));
};

toast.success = success;
toast.error = error;
toast.catch = catchFn;
toast.promise = promise;
toast.loading = loading;
toast.info = info;
toast.dismiss = sonnerToast.dismiss;

const typedToast: Toast = toast;

export { typedToast as toast };
